<template>
  <div class="page page-app-config">
    <el-container class="page-container">
      <el-header class="page-header" height="56px">
        <div class="title">大模型配置</div>
        <div>
          <el-button size="small" @click="$router.back()">返回</el-button>
          <el-button
            type="primary"
            size="small"
            :loading="saving"
            @click="handleSubmit"
          >
            保存配置
          </el-button>
        </div>
      </el-header>

      <el-main class="page-main">
        <el-form
          ref="form"
          :model="form"
          :rules="rules"
          label-width="120px"
          label-position="left"
          size="small"
          class="config-form"
        >
          <el-card shadow="never" class="section-card">
            <div slot="header" class="section-header">
              <span>基础信息</span>
            </div>
            <el-form-item label="是否启用" prop="llmEnable">
              <el-switch
                v-model="form.llmEnable"
                :active-value="1"
                :inactive-value="0"
              />
            </el-form-item>
            <el-form-item label="模型提供方" prop="llmProvider">
              <el-select v-model="form.llmProvider" placeholder="请选择提供方" filterable>
                <el-option label="OpenAI / 兼容 OpenAI" value="openai" />
                <el-option label="DeepSeek" value="deepseek" />
                <el-option label="通义千问（Qwen）" value="qwen" />
                <el-option label="其他 / 自建服务" value="custom" />
              </el-select>
            </el-form-item>
            <el-form-item label="模型名称" prop="llmModel">
              <el-input
                v-model="form.llmModel"
                placeholder="例如：gpt-4.1, deepseek-chat, qwen-max 等"
              />
            </el-form-item>
          </el-card>

          <el-card shadow="never" class="section-card">
            <div slot="header" class="section-header">
              <span>连接与鉴权</span>
            </div>
            <el-form-item label="API Base URL" prop="llmApiBase">
              <el-input
                v-model="form.llmApiBase"
                placeholder="例如：https://api.openai.com/v1"
              />
            </el-form-item>
            <el-form-item label="API Key" prop="llmApiKey">
              <el-input
                v-model="form.llmApiKey"
                type="password"
                show-password
                placeholder="请输入大模型服务的 API Key"
              />
            </el-form-item>
          </el-card>

          <el-card shadow="never" class="section-card">
            <div slot="header" class="section-header">
              <span>调用参数</span>
            </div>
            <el-row :gutter="20">
              <el-col :span="8">
                <el-form-item label="温度 (temperature)" prop="llmTemperature">
                  <el-input-number
                    v-model="form.llmTemperature"
                    :min="0"
                    :max="1"
                    :step="0.05"
                    :precision="2"
                    controls-position="right"
                  />
                </el-form-item>
              </el-col>
              <el-col :span="8">
                <el-form-item label="最大 Tokens" prop="llmMaxTokens">
                  <el-input-number
                    v-model="form.llmMaxTokens"
                    :min="256"
                    :max="32768"
                    :step="256"
                    controls-position="right"
                  />
                </el-form-item>
              </el-col>
              <el-col :span="8">
                <el-form-item label="超时时间（秒）" prop="llmTimeout">
                  <el-input-number
                    v-model="form.llmTimeout"
                    :min="5"
                    :max="300"
                    :step="5"
                    controls-position="right"
                  />
                </el-form-item>
              </el-col>
            </el-row>
          </el-card>
        </el-form>
      </el-main>
    </el-container>
  </div>
</template>

<script>
import { getAppConfig, updateAppConfig } from '@/api/appConfig';

export default {
  name: 'AppConfig',
  data() {
    return {
      loading: false,
      saving: false,
      form: {
        id: null,
        llmEnable: 1,
        llmProvider: 'openai',
        llmModel: '',
        llmApiBase: '',
        llmApiKey: '',
        llmTemperature: 0.7,
        llmMaxTokens: 2048,
        llmTimeout: 60
      },
      rules: {
        llmProvider: [{ required: true, message: '请选择模型提供方', trigger: 'change' }],
        llmModel: [{ required: true, message: '请输入模型名称', trigger: 'blur' }],
        llmApiBase: [{ required: true, message: '请输入 API Base URL', trigger: 'blur' }],
        llmApiKey: [{ required: true, message: '请输入 API Key', trigger: 'blur' }],
        llmTemperature: [{ required: true, message: '请设置 temperature', trigger: 'change' }],
        llmMaxTokens: [{ required: true, message: '请设置最大 tokens 数', trigger: 'change' }],
        llmTimeout: [{ required: true, message: '请设置超时时间', trigger: 'change' }]
      }
    };
  },
  created() {
    this.loadConfig();
  },
  methods: {
    async loadConfig() {
      this.loading = true;
      try {
        const res = await getAppConfig();
        const data = res.data || {};
        if (data) {
          this.form = Object.assign({}, this.form, data);
        }
      } catch (e) {
        // 已由全局拦截器提示
      } finally {
        this.loading = false;
      }
    },
    handleSubmit() {
      this.$refs.form.validate(async valid => {
        if (!valid) return;
        if (!this.form.id) {
          this.$message.error('配置ID缺失，请先在数据库中初始化 biz_app_config 记录');
          return;
        }
        this.saving = true;
        try {
          await updateAppConfig(this.form);
          this.$message.success('保存成功');
        } catch (e) {
          // 已由全局拦截器提示
        } finally {
          this.saving = false;
        }
      });
    }
  }
};
</script>

<style scoped>
.page-app-config {
  height: 100%;
  background: #f5f7fa;
  color: #303133;
}

.page-container {
  height: 100%;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  border-bottom: 1px solid #ebeef5;
  background: #ffffff;
}

.title {
  font-size: 16px;
  font-weight: 600;
}

.page-main {
  padding: 16px 24px 24px;
  overflow-y: auto;
}

.config-form {
  max-width: 960px;
  margin: 0 auto;
}

.section-card {
  margin-bottom: 16px;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 14px;
  font-weight: 500;
}
</style>

